/**
 * The snooze picker: a button that opens the Fibonacci ladder as a menu.
 *
 * Every option shows its label ("2 weeks") and its concrete return date. The
 * rung after the row's last snooze is preselected, so a second snooze of the
 * same item escalates by Enter alone. Digits 1–9 and 0 jump to the first ten
 * rungs; ↑/↓ move; Enter picks; Escape closes.
 *
 * `open` / `onOpenChange` are optional — pass them to open the menu from a
 * keyboard shortcut (`s` on a list row or the detail page).
 */
import { useState } from "react";
import { clsx } from "clsx";

import type { AdminReviewDoc } from "../../api/admin";
import { Menu, MenuItem } from "../ui/Menu";
import { useMenuTrigger } from "../ui/useMenuTrigger";
import { SNOOZE_LADDER, defaultRung, formatReturnDate, returnDate, snoozeLabel } from "../../lib/snooze";

type SnoozeDoc = Pick<AdminReviewDoc, "queue" | "snooze_days" | "snooze_count">;

interface Props {
  doc: SnoozeDoc;
  onPick: (days: number) => void;
  disabled?: boolean;
  /** Controlled open state (keyboard shortcuts); omit for a plain button. */
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  className?: string;
  label?: string;
}

export function SnoozeControl({
  doc,
  onPick,
  disabled,
  open: openProp,
  onOpenChange,
  className,
  label = "Snooze",
}: Props) {
  const trigger = useMenuTrigger();
  const open = openProp ?? trigger.open;
  const setOpen = (next: boolean) => {
    if (onOpenChange) onOpenChange(next);
    else trigger.setOpen(next);
  };
  const preselect = SNOOZE_LADDER.indexOf(defaultRung(doc) as (typeof SNOOZE_LADDER)[number]);

  return (
    <>
      <button
        {...trigger.triggerProps}
        aria-expanded={open}
        aria-controls={open ? trigger.menuId : undefined}
        onClick={() => setOpen(!open)}
        onKeyDown={(e) => {
          if (e.key === "ArrowDown" || e.key === "ArrowUp") {
            e.preventDefault();
            setOpen(true);
          }
        }}
        disabled={disabled}
        className={clsx("btn btn-ghost btn-sm", className)}
        title="Snooze – defer this and bring it back later"
      >
        {label}
      </button>
      <Menu
        open={open}
        onClose={() => setOpen(false)}
        anchorRef={trigger.triggerRef}
        id={trigger.menuId}
        aria-label="Snooze until"
        placement="bottom-end"
        focus={preselect < 0 ? 0 : preselect}
        className="snooze-menu"
        onKeyDown={(e, items) => {
          if (!/^[0-9]$/.test(e.key) || e.metaKey || e.ctrlKey || e.altKey) return;
          e.preventDefault();
          items[e.key === "0" ? 9 : Number(e.key) - 1]?.focus();
        }}
      >
        <SnoozeOptions doc={doc} onPick={onPick} preselect={preselect} />
      </Menu>
    </>
  );
}

/** Mounted on every open, so the dates are computed against that moment. */
function SnoozeOptions({
  doc,
  onPick,
  preselect,
}: {
  doc: SnoozeDoc;
  onPick: (days: number) => void;
  preselect: number;
}) {
  const [nowMs] = useState(() => Date.now());
  return (
    <>
      <div className="menu-label">Snooze until</div>
      {SNOOZE_LADDER.map((days, i) => (
        <MenuItem
          key={days}
          onSelect={() => onPick(days)}
          className={clsx("snooze-option", i === preselect && "is-suggested")}
        >
          <span className="snooze-key">{i < 9 ? i + 1 : i === 9 ? 0 : ""}</span>
          <span className="snooze-label">{snoozeLabel(days)}</span>
          <span className="snooze-date">{formatReturnDate(returnDate(days, nowMs), nowMs)}</span>
        </MenuItem>
      ))}
      {doc.snooze_count ? (
        <div className="menu-label">
          Snoozed {doc.snooze_count}× before
          {doc.snooze_days ? ` · last ${snoozeLabel(doc.snooze_days)}` : ""}
        </div>
      ) : null}
    </>
  );
}
